import React from 'react';
import { Box, useMediaQuery } from '@mui/material';
import { COLORS } from '@/constants';
import { Col } from '@/components/grid';
import getVehicleEmissionStarRating from '@/utils/get-vehicle-emission-star-rating';
import OptionalSafetyRow from '@/components/scorecard/components/optional-safety-row';
import { useEffectiveCO2Emissions } from '@/hooks/use-effective-co2-emissions';
import { useTheme } from '@mui/system';
import { ExtraSmall } from '@/components/type';
import { VehicleEcoData } from '@/types/vehicle-api/graphql';
import { HsiycSearchVehiclesRecord } from '@/types/legacy-api/graphql';

type EfficiencyAtAGlanceContentProps = {
  vehicleEcoData: VehicleEcoData;
  pageVehicle: HsiycSearchVehiclesRecord;
};

const EfficiencyAtAGlanceContent = ({ vehicleEcoData, pageVehicle }: EfficiencyAtAGlanceContentProps) => {
  const theme = useTheme();
  const smUp = useMediaQuery(theme.breakpoints.up('sm'));
  const smUpSpacing = !smUp ? 0 : undefined;

  const { powerplanttype, fuelcombined, energyconsumption, kmrangeelectricwltp } = vehicleEcoData;
  const { fuelType, transmission } = pageVehicle;

  const co2combined = useEffectiveCO2Emissions({
    // @ts-expect-error [🤖 PLEASE FIX 🤖]: Type 'Maybe<string> | undefined' is not assignable to type 'string | null'.
    powerplanttype,
    // @ts-expect-error [🤖 PLEASE FIX 🤖]: Type 'Maybe<number> | undefined' is not assignable to type 'number | null'.
    co2combined: vehicleEcoData.co2combined,
  });

  return (
    <Box p={6} display="flex" flexDirection={smUp ? 'row' : 'column'}>
      <Col
        className={undefined}
        spacing={smUpSpacing}
        offset={undefined}
        width={undefined}
        maxWidth={smUp ? '50%' : '100%'}
      >
        <OptionalSafetyRow label="Power Type" feature={powerplanttype} />
        <OptionalSafetyRow label="Transmission" feature={transmission} />
        <OptionalSafetyRow label="Fuel Type" feature={fuelType} />
      </Col>
      <Col
        className={undefined}
        spacing={smUpSpacing}
        offset={undefined}
        width={undefined}
        maxWidth={smUp ? '50%' : '100%'}
      >
        <OptionalSafetyRow
          label="Energy Consumption"
          feature={energyconsumption}
          unit="Wh/km"
          tooltip={
            <ExtraSmall>
              Measured in official laboratory testing, energy consumption indicates a vehicles electricity consumption
              per km travelled. Actual energy consumption may vary depending on driving habits and environmental
              conditions.
            </ExtraSmall>
          }
        />
        <OptionalSafetyRow
          label="Range"
          feature={kmrangeelectricwltp}
          unit="km"
          tooltip={
            <ExtraSmall>
              Measured in official laboratory testing, range indicates the estimated distance a vehicle can travel on
              battery power, if the battery is fully charged. Actual battery range may vary depending on driving habits
              and environmental conditions.
            </ExtraSmall>
          }
        />
        <OptionalSafetyRow
          label="Fuel Consumption"
          feature={fuelcombined}
          unit="L/100km"
          tooltip={
            <ExtraSmall>
              Measured in official laboratory testing, fuel consumption indicates a vehicles fuel consumption per 100km
              during ‘combined’ cycle testing. Actual fuel consumption may vary depending on driving habits and
              environmental conditions.
            </ExtraSmall>
          }
        />
        <OptionalSafetyRow
          label="CO₂ Combined"
          feature={co2combined}
          unit="g/km"
          tooltip={
            <ExtraSmall>
              Measured in official laboratory testing, CO2 emissions based on the ‘combined’ cycle indicates the
              vehicles carbon dioxide emissions per km. How you use your vehicle will influence actual emissions.
            </ExtraSmall>
          }
        />
        {co2combined !== null && co2combined >= 0 && (
          <OptionalSafetyRow
            label="Emission rating"
            feature={getVehicleEmissionStarRating(co2combined as number)}
            starColor={COLORS.BRAND_BLUE_NINE}
            maxStars={6}
            starAriaDescription="Emission rating"
            tooltip={<ExtraSmall>More stars mean lower emissions and less impact to the environment.</ExtraSmall>}
          />
        )}
      </Col>
    </Box>
  );
};

export default EfficiencyAtAGlanceContent;
